import type { LogtoErrorCode } from '@logto/phrases';
import { experience, type RequestErrorBody } from '@logto/schemas';
import { HTTPError, TimeoutError } from 'ky';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useNavigateWithPreservedSearchParams from './use-navigate-with-preserved-search-params';
import useToast from './use-toast';

export type ErrorHandlers = {
  [key in LogtoErrorCode]?: (error: RequestErrorBody) => void | Promise<void>;
} & {
  // Overwrite default global error handle logic
  global?: (error: RequestErrorBody) => void | Promise<void>;
};

/**
 * Built-in handlers for terminal error codes that should land on a dedicated page
 * instead of a transient toast. Specific per-call handlers still take precedence;
 * these only run when the caller did not handle the code and before falling back
 * to `global` / toast.
 */
const getTerminalErrorPath = (code: LogtoErrorCode): string | undefined => {
  if (code === 'user.suspended') {
    return `/${experience.routes.accountSuspended}`;
  }

  return undefined;
};

/**
 * TPB spec-OIDC recovery — oidc session / connector-session codes that must
 * recover to a fresh sign-in rather than dead-end on a toast.
 *
 * `session.not_found` is the intermittent "Session non trouvée" screen users
 * hit when the Connect2id multi-account translator (patch 010) injects a
 * stale/destroyed `_session` cookie (a slot expired, or a cross-flow race left
 * a cookie pointing at a session oidc-provider no longer has). Per OIDC an
 * absent session simply means "re-authenticate" — surfacing it as a dead-end is
 * wrong. The connector-session variants are the same class mid-social-flow.
 *
 * `interaction_not_found` is intentionally EXCLUDED: the whole interaction is
 * gone, so /sign-in has nothing to resume — the RP must re-initiate authorize.
 */
const recoverableSessionCodes = new Set<LogtoErrorCode>([
  'session.not_found',
  'session.connector_session_not_found',
  'session.connector_validation_session_not_found',
]);

const recoverSignIn = (code: string): void => {
  // BROWSER-console marker (this hook runs in the experience SPA, NOT the server).
  // It records that a CLIENT recovery fired — visible in the browser devtools /
  // via tpb-browser. The SERVER-side observability of the underlying
  // session-not-found (grepable in `fly logs`) is patch 020 (koa-error-handler).
  // eslint-disable-next-line no-console
  console.warn(
    `[session-recovery] ${code} -> restarting sign-in (spec-OIDC: absent session => re-authenticate)`
  );
  const { pathname, search } = window.location;
  // Guard against a redirect loop: the sign-in entry does not assert an existing
  // session, so if we are already there just let it render.
  if (/\/sign-in\/?$/.test(pathname)) {
    return;
  }
  // Hard redirect (this hook has no router). The `_interaction` cookie carries
  // the current interaction, so /sign-in resumes it with a fresh authentication;
  // the multi-account translator self-heals the stale slot/_session on the next
  // /oidc/* hop (removeSlot + stripSessionCookies).
  window.location.replace(`/sign-in${search}`);
};

const useErrorHandler = () => {
  const { t } = useTranslation();
  const { setToast } = useToast();
  const navigate = useNavigateWithPreservedSearchParams();

  const handleError = useCallback(
    async (error: unknown, errorHandlers?: ErrorHandlers) => {
      if (error instanceof HTTPError) {
        try {
          const logtoError = await error.response.json<RequestErrorBody>();

          const { code, message } = logtoError;
          const terminalPath = getTerminalErrorPath(code);

          // TPB spec-OIDC recovery: never dead-end on an absent/expired session.
          // Fires only when the page did not register its OWN handler for this
          // code (a page can still opt out by providing an explicit per-code
          // handler); global fallbacks and the toast are bypassed for this class.
          if (!errorHandlers?.[code] && recoverableSessionCodes.has(code)) {
            recoverSignIn(code);

            return;
          }

          const handler =
            errorHandlers?.[code] ??
            (terminalPath
              ? () => {
                  navigate(terminalPath, { replace: true });
                }
              : errorHandlers?.global);

          if (handler) {
            await handler(logtoError);
          } else {
            setToast(message);
          }

          return;
        } catch (error) {
          setToast(t('error.unknown'));
          console.error(error);

          return;
        }
      }

      if (error instanceof TimeoutError) {
        setToast(t('error.timeout'));

        return;
      }

      setToast(t('error.unknown'));
      console.error(error);
    },
    [navigate, setToast, t]
  );

  return handleError;
};

export default useErrorHandler;
